import User from "@/types/user"
import { Ref, ref } from "vue"
import { useAppFlasher } from "@xy-planning-network/trees"
import logger from "./logger"
import { initLinkTag, initScriptTag } from "./browser"

const calendlyStyle = "https://assets.calendly.com/assets/external/widget.css"
const calendlyScript = "https://assets.calendly.com/assets/external/widget.js"

// used to ensure only one listener is ever attached to the window
// for "message" events duplicate listeners can be added if the calendly pop
// up is opened and closed multiple times by a user.
let hasCalendlyMessageListener = false

/**
 * Calendly does not make types avaialable for window.Calendly
 * This is a partial interface that we currently expect to be available
 * scoped to only the methods and properties what we expect to use.
 * The actual methods and properties available on window.Calendly may change
 * as the Calendly API is updated.
 */
interface Calendly {
  closePopupWidget(): void
  initPopupWidget(options: CalendlyOptions): void
}

/**
 * CalendlyOptions are the currently expected options that can be
 * passed to Calendly's widget initialization methods.  These options
 * may change as the Calendly API changes.
 */
interface CalendlyOptions {
  url: string
  prefill: {
    customAnswers: Record<string, string | number>
    email: string
    name: string
  }
  utm?: {
    utmSource: string
    utmMedium: string
  }
}

/**
 * CalendlyMsgEventScheduled is the currently expected data property
 * value of the MessageEvent emitted from the Calendly iframe.
 * This interface may change as the Calendly API is updated.
 */
interface CalendlyMsgEventScheduled {
  event: "calendly.event_scheduled"
  payload: {
    event: {
      uri: string
    }
    invitee: {
      uri: string
    }
  }
}

/**
 * initCalendlyAssets injects the necessary Calendly hosted
 * stylesheet and javascript dependencies.  Ensuring that it
 * only occurs once.  The returning Calendly object is a copy
 * of the instance that Calendly's script on the global window.
 * @return Promise<Calendly>
 */
const initCalendlyAssets: () => Promise<Calendly> = (() => {
  let once: Promise<Calendly> | undefined

  const init = async (): Promise<Calendly> => {
    const script = await initScriptTag(calendlyScript).then(() => {
      const calendly = (window as any).Calendly as Calendly
      return Promise.resolve({ ...calendly })
    })

    const style = await initLinkTag(calendlyStyle).then(() => {
      return Promise.resolve()
    })

    return Promise.all([script, style]).then(([calendly]) => {
      return calendly
    })
  }

  return async () => {
    if (!once) {
      once = init()
    }

    return once
  }
})()

export interface UseCalendly {
  /**
   * closePopup removes message event listeners and triggers the Calendly
   * interal closePopup method.  Flashing a generic error if a failure occurs.
   * @returns void
   */
  closePopup(): void
  /**
   * isLoaded tracks the state of Calendly external assets being loaded and ready.
   * Because assets are loaded once this ref may be returned with an immediate truthy value.
   */
  isLoaded: Ref<boolean>
  /**
   * loadingError tracks the error state of loading any Calendly external assets.
   * Because assets are loaded only once this ref may be returned with an immediate truthy value.
   */
  loadingError: Ref<boolean>
  /**
   * openPopup adds a message event listener before triggering the internal Calendly
   * openPopup method.  Flashing a generic error if a failure occurs.
   * @returns void
   */
  openPopup(): void
  /**
   *
   * @param callback function when the calendly.event_scheduled message is received
   * from the Calendly iframe. It passes a single argument
   */
  onScheduled(callback: (url: string) => void): void
}

export interface UseCalendlyConfig {
  customAnswers?: Record<string, string | number>
  /**
   * url is the Calendly Booking URL to display the overlay for
   */
  url: string
  /**
   * user will prefill the name and email fields that exist on all Calendly popup forms
   */
  user?: User
  /**
   * utmSource will apply a Google Analytics utmSource value when GA integration exists on the account
   * documentation on this feature is minimal
   */
  utmSource?: string
  /**
   * utmMedium will apply a Google Analytics utmMedium value when GA integration exists on the account
   * documentation on this feature is minimal
   */
  utmMedium?: string
}

/**
 * useCalendly is a composable function that initializes
 * the necessary dependencies for working with Calendly's
 * popup widget along with useful callbacks and ref's
 * for composing Calendly popup widget functionality
 * @param config UseCalendlyConfig
 * @param onReady optional callback once Calendly assets are loaded
 * @param onError optional callback if one or more Calendly assets fails to load
 * @returns UseCalendly
 */
export const useCalendly = (
  config: UseCalendlyConfig,
  onReady?: () => void,
  onError?: (e: unknown) => void
): UseCalendly => {
  config = {
    customAnswers: {},
    user: null,
    utmSource: "XYPN",
    utmMedium: "",
    ...config,
  }

  const opts: CalendlyOptions = {
    url: config.url,
    prefill: {
      email: config.user?.email || "",
      name: config.user?.fullName || "",
      customAnswers: config.customAnswers,
    },
    utm: {
      utmSource: config.utmSource,
      utmMedium: config.utmMedium,
    },
  }

  const isLoaded = ref(false)
  const loadingError = ref(false)

  let calendly: Calendly | undefined

  let onScheduledCb: (event: MessageEvent) => void | undefined

  const onScheduled = (cb: (url: string) => void) => {
    onScheduledCb = (event: MessageEvent) => {
      if (isEventScheduled(event)) {
        cb(event.data.payload.event.uri)
      }
    }
  }

  const isEventScheduled = (
    msg: MessageEvent
  ): msg is MessageEvent<CalendlyMsgEventScheduled> => {
    return (
      msg.origin === "https://calendly.com" &&
      msg.data?.event === "calendly.event_scheduled"
    )
  }

  // Existing use cases are all likely to benefit from the convenience of baked in error handling
  // and a genericError flash to the user if this call fails.  This flash is a convenience for
  // existing use cases, but should not be held on to if future use cases want to avoid it.
  // In the future it can be refactored to return a Promise where the consumer
  // can handle errors explicitly.
  const openPopup = (): void => {
    try {
      calendly.initPopupWidget(opts)
      if (!hasCalendlyMessageListener) {
        window.addEventListener("message", onScheduledCb)
        hasCalendlyMessageListener = true
      }
    } catch (err) {
      useAppFlasher.genericError()
      logger.warn(`Unexpected error opening calendly widget: ${err}`)
    }
  }

  // The Calendly api is the top reason that this method could throw an
  // unexpected error, but even in that case the user will have the option to close the
  // existing window, so in nearly all cases, we can likely avoid additional UI state (i.e. Flashes)
  // to let the user know something went wrong.
  const closePopup = (): void => {
    try {
      calendly.closePopupWidget()
      window.removeEventListener("message", onScheduledCb)
      hasCalendlyMessageListener = false
    } catch (err) {
      logger.warn(`Unexpected error opening calendly widget: ${err}`)
    }
  }

  initCalendlyAssets()
    .then((c) => {
      calendly = c
      isLoaded.value = true
      onReady ? onReady() : undefined
    })
    .catch((err: unknown) => {
      logger.info(`Unable to load calendly assets: ${err}`, { config })
      loadingError.value = true
      onError ? onError(err) : undefined
    })

  return {
    closePopup,
    isLoaded,
    loadingError,
    onScheduled,
    openPopup,
  }
}
